import { getValidatedTourConfig } from "@/lib/tour-configs";
import { logTourValidation } from "@/lib/tour-validation";
import { TOUR_TYPES, TourType, useTourStore } from "@/store/tour-store";
import React, { ReactNode, useEffect } from "react";
import Joyride, { CallBackProps, STATUS } from "react-joyride";

interface JoyrideProviderProps {
  children: ReactNode;
}

export const JoyrideProvider: React.FC<JoyrideProviderProps> = ({
  children,
}) => {
  const { activeTours, initializeTours, endTour, skipTour } = useTourStore();

  // Initialize tours from localStorage on mount
  useEffect(() => {
    initializeTours();

    // Log tour validation in development
    if (process.env.NODE_ENV === "development") {
      setTimeout(() => {
        logTourValidation();
      }, 2000); // Wait for components to render
    }
  }, [initializeTours]);

  const handleJoyrideCallback =
    (tourType: TourType) => (data: CallBackProps) => {
      const { status } = data;
      if (
        [STATUS.FINISHED, STATUS.SKIPPED].includes(
          status as "finished" | "skipped"
        )
      ) {
        if (status === STATUS.SKIPPED) {
          skipTour(tourType);
        } else {
          endTour(tourType);
        }
      }
    };

  // Render all active tours
  const renderTours = () => {
    const tours: JSX.Element[] = [];
    let firstActive: TourType | null = null;

    // Render predefined tours
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(TOUR_TYPES).forEach(([_, tourType]: [string, TourType]) => {
      const validatedConfig = getValidatedTourConfig(tourType);
      if (!validatedConfig) {
        return;
      }

      // Skip tours with missing targets
      if (!validatedConfig.validation.valid) {
        return;
      }

      if (!firstActive && !!activeTours[tourType]) {
        firstActive = tourType;
      }

      tours.push(
        <Joyride
          key={tourType}
          steps={validatedConfig.steps}
          run={firstActive === tourType}
          continuous
          showProgress
          showSkipButton
          styles={validatedConfig.styles}
          callback={handleJoyrideCallback(tourType)}
          disableScrolling={false}
          disableOverlayClose={true}
          spotlightPadding={4}
          locale={{
            last: "Done",
            skip: "Skip",
          }}
        />
      );
    });

    return tours;
  };

  return (
    <div>
      {renderTours()}
      {children}
    </div>
  );
};
